import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Flag, Ban, Trash2, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const REASON_LABEL = {
    harassment: "Harassment",
    inappropriate: "Inappropriate content",
    fake_profile: "Fake profile",
    scam: "Scam / fraud",
    underage: "Underage",
    other: "Other",
};

export default function AdminReports() {
    const [status, setStatus] = useState("open");
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(0);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/admin/reports", { params: { status, limit: 100 } });
            setItems(data.items || []);
            setOpen(data.open || 0);
        } catch (e) { toast.error("Couldn't load reports"); }
        finally { setLoading(false); }
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const act = async (reportId, action, name) => {
        if (action !== "dismiss" && !window.confirm(`${action === "ban_user" ? "Ban" : "Delete"} ${name || "this user"}? This resolves all open reports against them.`)) return;
        setActing(reportId);
        try {
            await adminApi.post(`/admin/reports/${reportId}/action`, { action });
            toast.success(action === "dismiss" ? "Dismissed" : action === "ban_user" ? "User banned" : "User deleted");
            load();
        } catch (e) { toast.error("Action failed"); }
        finally { setActing(null); }
    };

    return (
        <div data-testid="admin-reports-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-black flex items-center gap-2">
                        <Flag className="h-7 w-7 text-[#E85D5D]" /> Reports
                    </h1>
                    <p className="text-sm text-white/50">{open} open report{open === 1 ? "" : "s"}</p>
                </div>
                <div className="inline-flex rounded-2xl border border-white/10 bg-white/5 p-1">
                    {["open", "resolved"].map((t) => (
                        <button
                            key={t}
                            onClick={() => setStatus(t)}
                            data-testid={`admin-reports-tab-${t}`}
                            className={`px-4 py-1.5 rounded-xl text-sm font-bold capitalize transition ${status === t ? "bg-[#E85D5D] text-white" : "text-white/60 hover:text-white"}`}
                        >
                            {t}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-4 space-y-3" data-testid="admin-reports-list">
                {loading ? (
                    <div className="text-white/50 text-sm">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-white/10 p-8 text-center text-white/50">
                        <div className="text-4xl mb-2">🛡️</div>
                        No {status} reports.
                    </div>
                ) : items.map((r) => (
                    <div key={r.id} className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`admin-report-row-${r.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-[#EF4444]/20 text-[#EF4444] flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" strokeWidth={3} /> {REASON_LABEL[r.reason] || r.reason}
                            </span>
                            {r.reported_total > 1 && (
                                <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-[#F59E0B]/20 text-[#F59E0B]">
                                    {r.reported_total}× reported
                                </span>
                            )}
                            <span className="text-xs text-white/40">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                        </div>

                        <div className="mt-3 flex items-center gap-2 text-sm">
                            <span className="text-white/50">Reported:</span>
                            <span className="font-bold">{r.reported?.name || "—"}</span>
                            <span className="text-white/40 text-xs">{r.reported?.email}</span>
                            {r.reported?.banned && <span className="text-[10px] text-[#EF4444] font-black uppercase">banned</span>}
                        </div>
                        <div className="text-xs text-white/40">by {r.reporter?.name || r.reporter?.email || "anon"}</div>
                        {r.details && <div className="mt-2 rounded-xl bg-black/40 p-3 text-sm text-white/90 break-words">{r.details}</div>}

                        {r.status === "open" ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                                <button onClick={() => act(r.id, "dismiss")} disabled={acting === r.id}
                                    data-testid={`admin-report-dismiss-${r.id}`}
                                    className="h-9 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 px-3 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
                                    <Check className="h-3.5 w-3.5" /> Dismiss
                                </button>
                                <button onClick={() => act(r.id, "ban_user", r.reported?.name)} disabled={acting === r.id}
                                    data-testid={`admin-report-ban-${r.id}`}
                                    className="h-9 rounded-lg bg-[#EF4444]/20 border border-[#EF4444]/40 hover:bg-[#EF4444]/30 text-[#EF4444] px-3 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
                                    <Ban className="h-3.5 w-3.5" /> Ban user
                                </button>
                                <button onClick={() => act(r.id, "delete_user", r.reported?.name)} disabled={acting === r.id}
                                    data-testid={`admin-report-delete-${r.id}`}
                                    className="h-9 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 px-3 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
                                    <Trash2 className="h-3.5 w-3.5" /> Delete user
                                </button>
                            </div>
                        ) : (
                            <div className="mt-3">
                                <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-white/10 text-white/50">
                                    {r.resolution || "resolved"}
                                </span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
